import React from 'react';
import { TouchableOpacity, ViewStyle } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import styled from 'styled-components/native';
import Icon from 'react-native-vector-icons/MaterialIcons';
import { getElevationStyle } from '@theme/aiaTheme';
import { useTheme } from '@theme/ThemeProvider';
import { Text } from './Text';

interface AppBarProps {
  title?: string;
  leftIcon?: string;
  onLeftPress?: () => void;
  rightIcon?: string;
  onRightPress?: () => void;
  rightElement?: React.ReactNode;
  transparent?: boolean;
  style?: ViewStyle;
}

const Container = styled(SafeAreaView)<{ transparent: boolean }>`
  background-color: ${({ theme, transparent }) =>
    transparent ? theme.colors.transparent : theme.colors.surface};
  ${({ theme, transparent }) => (transparent ? '' : getElevationStyle(theme.elevation.sm))};
`;

const Content = styled.View`
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  height: 56px;
  padding-horizontal: ${({ theme }) => theme.spacing.xs}px;
`;

const LeftSection = styled.View`
  flex-direction: row;
  align-items: center;
  flex: 1;
`;

const CenterSection = styled.View`
  flex: 2;
  align-items: center;
`;

const RightSection = styled.View`
  flex-direction: row;
  align-items: center;
  justify-content: flex-end;
  flex: 1;
`;

const IconButton = styled(TouchableOpacity)`
  width: ${({ theme }) => theme.minTouchTarget}px;
  height: ${({ theme }) => theme.minTouchTarget}px;
  align-items: center;
  justify-content: center;
`;

export const AppBar: React.FC<AppBarProps> = ({
  title,
  leftIcon,
  onLeftPress,
  rightIcon,
  onRightPress,
  rightElement,
  transparent = false,
  style,
}) => {
  const theme = useTheme();

  return (
    <Container transparent={transparent} edges={['top']} style={style}>
      <Content>
        <LeftSection>
          {leftIcon && (
            <IconButton onPress={onLeftPress}>
              <Icon name={leftIcon} size={24} color={theme.colors.textPrimary} />
            </IconButton>
          )}
        </LeftSection>
        <CenterSection>
          {title && (
            <Text variant="titleMedium" color="textPrimary" numberOfLines={1}>
              {title}
            </Text>
          )}
        </CenterSection>
        <RightSection>
          {rightElement}
          {rightIcon && (
            <IconButton onPress={onRightPress}>
              <Icon name={rightIcon} size={24} color={theme.colors.textPrimary} />
            </IconButton>
          )}
        </RightSection>
      </Content>
    </Container>
  );
};
